import { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  TextField,
  Button,
  IconButton,
  Chip,
  Radio,
  Checkbox,
  Alert,
  RadioGroup,
  FormControlLabel,
  FormControl,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  InputAdornment,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import {
  ExpandMore,
  Close as CloseIcon,
  Save as SaveIcon,
  CalendarMonth,
  Weekend,
  Assessment,
  Toll,
} from "@mui/icons-material";

import { estacionesDe, daysFromFrequency } from "./configurationModel";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";

const DIAS = ["Lun", "Mar", "Mie", "Jue", "Vie", "Sab", "Dom"];

const TIPOS_CORTE = [
  { value: "calendario", label: "Calendario (00:00 - 23:59)" },
  { value: "turno_6am", label: "Turno 6 AM (06:00 - 05:59)" },
  { value: "turno_6am_especial", label: "Turno 6 AM Especial" },
  { value: "turno_7am", label: "Turno 7 AM (07:00 - 07:00)" },
  { value: "dia_trunc", label: "Día Truncado" },
];

const TIPOS_RETEICA = [
  { value: "fijo", label: "Fijo" },
  { value: "por_comercio", label: "Por Comercio" },
];

const VALIDACIONES_FALLBACK = [
  { value: "NO SE PAGA", label: "NO SE PAGA" },
  { value: "DISPUTA", label: "DISPUTA" },
];

const parseDias = (str) => {
  if (!str) return [];
  return DIAS.filter((d) => str.includes(d));
};

// 🔥 Estilo reutilizable para TODOS los inputs → altura 40px
const inputSx = {
  "& .MuiInputBase-root": {
    height: 40,
    fontSize: "0.85rem",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.8rem",
  },
};

const SectionTitle = ({ icon, children }) => (
  <MDBox
    display="flex"
    alignItems="center"
    gap={1}
    mb={2}
    pb={1}
    sx={{
      borderBottom: "1px solid",
      borderColor: "#e2e8f0",
    }}
  >
    {icon}
    <MDTypography
      variant="caption"
      sx={{
        fontWeight: 700,
        color: "#334155",
        fontSize: "0.82rem",
        letterSpacing: 0.1,
      }}
    >
      {children}
    </MDTypography>
  </MDBox>
);

SectionTitle.propTypes = {
  icon: PropTypes.node.isRequired,
  children: PropTypes.node,
};

const toggleGroupSx = (accent) => ({
  flexWrap: "wrap",
  gap: 0.75,
  "& .MuiToggleButton-root": {
    border: "1px solid #cbd5e1",
    borderRadius: "8px !important",
    px: 1.5,
    py: 0.75,
    minWidth: 40,
    height: 36,
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.78rem",
    color: "#475569",
    backgroundColor: "#fff",
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: alpha(accent, 0.06),
      borderColor: accent,
    },
    "&.Mui-selected": {
      bgcolor: alpha(accent, 0.12),
      color: accent,
      borderColor: accent,
      boxShadow: `0 0 0 1px ${alpha(accent, 0.35)} inset`,
      "&:hover": { bgcolor: alpha(accent, 0.18) },
    },
  },
});

const ConcesionModal = ({ open, onClose, concesion, onSave }) => {
  const [form, setForm] = useState(null);
  const [reteicaMapText, setReteicaMapText] = useState("{}");
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rulesExpanded, setRulesExpanded] = useState(false);
  const [saveError, setSaveError] = useState("");
  const savingRef = useRef(false);

  useEffect(() => {
    if (open && concesion) {
      const rules = concesion.rules || {};
      const politica = rules.politica_pago || {};
      const estaciones = estacionesDe(concesion);

      setForm({
        id: concesion.id,
        code: concesion.code,
        nombre: rules.nombre || concesion.name,
        // Parámetros financieros
        llave_fmt: rules.llave_fmt || "",
        tipo_corte: rules.tipo_corte || "calendario",
        pct_reteiva: rules.pct_reteiva ?? 0,
        pct_comision: rules.pct_comision ?? 0,
        reteica_fijo: rules.reteica_fijo ?? 0,
        tipo_reteica: rules.tipo_reteica || "fijo",
        pct_retefuente: rules.pct_retefuente ?? 0,
        reteica_default: rules.reteica_default ?? 0,
        pct_iva_comision: rules.pct_iva_comision ?? 0,
        validacion_ajuste_fallback: rules.validacion_ajuste_fallback || "NO SE PAGA",
        // Política de pago
        frecuencia_lv: politica.frecuencia_lv || "",
        dias_traslado_lv: politica.dias_traslado_lv || "",
        dias_habiles_lv: daysFromFrequency(politica.frecuencia_lv) ?? politica.dias_habiles_lv ?? 0,
        frecuencia_fds: politica.frecuencia_fds || "",
        dias_traslado_fds: politica.dias_traslado_fds || "",
        dias_habiles_fds:
          daysFromFrequency(politica.frecuencia_fds) ?? politica.dias_habiles_fds ?? 0,
        modalidad: politica.modalidad || "CONSOLIDADO",
        descripcion_modalidad: politica.descripcion_modalidad || "",
        // Estaciones
        estaciones: estaciones.map((e) => ({
          ...e,
          pago_independiente: politica.modalidad === "POR_PEAJE" ? true : !!e.pago_independiente,
        })),
        _original: concesion,
      });

      setReteicaMapText(JSON.stringify(rules.reteica_map || {}, null, 2));
      setSaveError("");
      setConfirmOpen(false);
      setRulesExpanded(false);
    }
  }, [open, concesion]);

  if (!concesion || !form) return null;

  const handleChange = (field, value) => {
    if (!savingRef.current)
      setForm((prev) => ({
        ...prev,
        [field]: value,
        ...(field === "modalidad"
          ? {
              estaciones: prev.estaciones.map((estacion) => ({
                ...estacion,
                pago_independiente: value === "POR_PEAJE",
              })),
            }
          : {}),
        ...(field === "frecuencia_lv" ? { dias_habiles_lv: daysFromFrequency(value) ?? "" } : {}),
        ...(field === "frecuencia_fds" ? { dias_habiles_fds: daysFromFrequency(value) ?? "" } : {}),
      }));
  };

  const handleDiasChange = (field, _event, newValue) => {
    handleChange(field, DIAS.filter((dia) => newValue.includes(dia)).join(" - "));
  };

  const handleEstacionChange = (index, field, value) => {
    if (savingRef.current || !["hora_inicio", "hora_fin", "pago_independiente"].includes(field))
      return;
    const newEstaciones = [...form.estaciones];
    newEstaciones[index] = { ...newEstaciones[index], [field]: value };
    setForm((prev) => ({ ...prev, estaciones: newEstaciones }));
  };

  const handleClose = () => {
    if (!savingRef.current) onClose();
  };

  const handleSubmit = async () => {
    if (savingRef.current || !onSave) return;
    setSaveError("");
    if (!String(form.code ?? "").trim()) {
      setSaveError("El código de la concesión es requerido.");
      return;
    }
    if (!["CONSOLIDADO", "POR_PEAJE"].includes(form.modalidad)) {
      setSaveError("Seleccione una modalidad válida.");
      return;
    }
    for (const field of ["dias_habiles_lv", "dias_habiles_fds"]) {
      const value = Number(form[field]);
      if (String(form[field]).trim() === "" || !Number.isInteger(value) || value < 0) {
        setSaveError("Indique una frecuencia válida, por ejemplo: Día hábil o 3 días hábiles.");
        return;
      }
    }
    const timePattern = /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/;
    const nombres = new Set();
    for (const estacion of form.estaciones) {
      if (!estacion.peaje?.trim()) {
        setSaveError("Todos los peajes deben tener un nombre.");
        return;
      }
      const nombre = estacion.peaje.trim().toLocaleLowerCase("es");
      if (nombres.has(nombre)) {
        setSaveError(
          `El peaje ${estacion.peaje} está repetido. Revise los nombres antes de guardar.`
        );
        return;
      }
      nombres.add(nombre);
      if (!timePattern.test(estacion.hora_inicio) || !timePattern.test(estacion.hora_fin)) {
        setSaveError(`Revise las horas de inicio y fin de ${estacion.peaje}.`);
        return;
      }
    }

    setConfirmOpen(true);
  };

  const confirmSave = async () => {
    if (savingRef.current || !onSave || !confirmOpen) return;
    const payloadCompleto = {
      modalidad: form.modalidad,
      descripcion_modalidad: form.descripcion_modalidad,
      frecuencia_lv: form.frecuencia_lv,
      dias_habiles_lv: Number(form.dias_habiles_lv),
      dias_traslado_lv: form.dias_traslado_lv,
      frecuencia_fds: form.frecuencia_fds,
      dias_habiles_fds: Number(form.dias_habiles_fds),
      dias_traslado_fds: form.dias_traslado_fds,
      estaciones_peajes: form.estaciones.map((est) => ({
        peaje: est.peaje,
        hora_inicio: est.hora_inicio,
        hora_fin: est.hora_fin,
        pago_independiente: form.modalidad === "POR_PEAJE" || !!est.pago_independiente,
      })),
    };

    savingRef.current = true;
    setConfirmOpen(false);
    setSaving(true);
    try {
      const result = await onSave(form.code, payloadCompleto);
      if (result?.success) {
        onClose();
      } else {
        setSaveError(result?.error || "No se pudo guardar. Puede reintentar.");
      }
    } catch (error) {
      setSaveError(error.message || "Error de conexión al guardar. Puede reintentar.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const esConsolidado = form.modalidad === "CONSOLIDADO";

  // 🔍 Determinar si el reteica_map tiene contenido real
  const reteicaMapTieneContenido =
    reteicaMapText.trim() !== "" &&
    reteicaMapText.trim() !== "{}" &&
    reteicaMapText.trim() !== "{\n}";

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        aria-labelledby="concesion-modal-title"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            maxHeight: "92vh",
            m: { xs: 1, sm: 2 },
            width: { xs: "calc(100% - 16px)", sm: "calc(100% - 32px)" },
          },
        }}
      >
        {/* HEADER */}
        <DialogTitle
          sx={{
            background: "linear-gradient(120deg,#1e293b,#0b4f6c)",
            color: "#fff",
            px: { xs: 2, sm: 3 },
            py: 2,
            m: 0,
            mb: 0,
          }}
        >
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  bgcolor: alpha("#fff", 0.18),
                  borderRadius: "12px",
                  p: 1.25,
                  display: "flex",
                }}
              >
                <Toll sx={{ fontSize: 24, color: "#fff" }} />
              </Box>
              <MDBox>
                <MDTypography
                  id="concesion-modal-title"
                  variant="h6"
                  color="white"
                  fontWeight="bold"
                  sx={{ lineHeight: 1.2 }}
                >
                  Configuración de la concesión
                </MDTypography>
                <MDTypography
                  variant="body2"
                  color="white"
                  sx={{ opacity: 0.85, mt: 0.5, display: "block" }}
                >
                  {form.nombre}
                </MDTypography>
              </MDBox>
            </MDBox>
            <IconButton
              aria-label="Cerrar configuración"
              onClick={handleClose}
              disabled={saving}
              sx={{
                color: "#fff",
                bgcolor: alpha("#fff", 0.08),
                "&:hover": { bgcolor: alpha("#fff", 0.2) },
              }}
            >
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>

        {/* BODY */}
        <DialogContent
          dividers
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 3 },
            borderColor: "#e2e8f0",
            mt: 0,
            bgcolor: "#f4f7fa",
          }}
        >
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}
          <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={2.5}>
            <Chip
              size="small"
              label={`Código: ${form.code ?? "—"}`}
              sx={{ bgcolor: "#e5edf4", color: "#334155", fontWeight: 600 }}
            />
            <Chip
              size="small"
              label={`${form.estaciones.length} peajes asociados`}
              variant="outlined"
            />
          </MDBox>
          {/* CONSOLIDADO */}
          <MDBox
            mb={{ xs: 2, md: 2.5 }}
            sx={{
              p: { xs: 2, sm: 2.5 },
              bgcolor: "#fff",
              border: "1px solid #dfe6ed",
              borderRadius: 2,
            }}
          >
            <SectionTitle icon={<Assessment sx={{ fontSize: 17, color: "#e65100" }} />}>
              Modalidad de pago
            </SectionTitle>

            <Box
              sx={{
                p: 0,
                borderRadius: 2,
                bgcolor: "#fff",
                border: 0,
              }}
            >
              <FormControl fullWidth>
                <RadioGroup
                  aria-label="Modalidad de pago"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1.5,
                  }}
                  value={form.modalidad}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleChange("modalidad", val);
                    handleChange(
                      "descripcion_modalidad",
                      val === "CONSOLIDADO"
                        ? "Un solo Pago por el total de todos los peajes"
                        : "Pago Independiente por el valor de cada peaje"
                    );
                  }}
                >
                  <FormControlLabel
                    value="CONSOLIDADO"
                    control={
                      <Radio
                        disabled={saving}
                        sx={{
                          color: "#cbd5e1",
                          "&.Mui-checked": { color: "#2e7d32" },
                        }}
                      />
                    }
                    label={
                      <MDTypography variant="body2" sx={{ fontSize: "0.88rem", fontWeight: 500 }}>
                        Un solo Pago por el total de todos los peajes
                      </MDTypography>
                    }
                    sx={{
                      m: 0,
                      borderRadius: 1.5,
                      px: 1.5,
                      py: 1,
                      border: "1px solid",
                      borderColor: esConsolidado ? "#75ad91" : "#dfe6ed",
                      bgcolor: esConsolidado ? "#eff8f2" : "#fff",
                      transition: "background 0.15s",
                      "&:hover": { bgcolor: alpha("#2e7d32", 0.04) },
                    }}
                  />
                  <FormControlLabel
                    value="POR_PEAJE"
                    control={
                      <Radio
                        disabled={saving}
                        sx={{
                          color: "#cbd5e1",
                          "&.Mui-checked": { color: "#e65100" },
                        }}
                      />
                    }
                    label={
                      <MDTypography variant="body2" sx={{ fontSize: "0.88rem", fontWeight: 500 }}>
                        Pago Independiente por el valor de cada peaje
                      </MDTypography>
                    }
                    sx={{
                      m: 0,
                      borderRadius: 1.5,
                      px: 1.5,
                      py: 1,
                      border: "1px solid",
                      borderColor: !esConsolidado ? "#dbad6c" : "#dfe6ed",
                      bgcolor: !esConsolidado ? "#fff7ec" : "#fff",
                      transition: "background 0.15s",
                      "&:hover": { bgcolor: alpha("#e65100", 0.04) },
                    }}
                  />
                </RadioGroup>
              </FormControl>

              <MDTypography
                component="p"
                variant="body2"
                aria-label="Descripción de modalidad"
                sx={{ mt: 2, color: "#475569", lineHeight: 1.6 }}
              >
                {form.descripcion_modalidad || "Sin descripción"}
              </MDTypography>

              {form.modalidad && (
                <MDBox mt={2} pt={1.5} borderTop="1px dashed #e2e8f0">
                  <Chip
                    size="small"
                    label={`Modo actual: ${esConsolidado ? "Consolidado" : "Independiente"}`}
                    sx={{
                      bgcolor: esConsolidado ? "#e8f5e9" : "#fff3e0",
                      color: esConsolidado ? "#2e7d32" : "#e65100",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      border: `1px solid ${esConsolidado ? "#a5d6a7" : "#ffcc80"}`,
                    }}
                  />
                </MDBox>
              )}
            </Box>
          </MDBox>

          <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
            <Grid item xs={12} md={6}>
              {/* LIQUIDACIÓN L-V */}
              <MDBox
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  height: "100%",
                  bgcolor: "#fff",
                  border: "1px solid #dfe6ed",
                  borderRadius: 2,
                }}
              >
                <SectionTitle icon={<CalendarMonth sx={{ fontSize: 17, color: "#2e7d32" }} />}>
                  Lunes a viernes
                </SectionTitle>
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <TextField
                      disabled={saving}
                      fullWidth
                      size="small"
                      label="Frecuencia de liquidación"
                      placeholder="Ej: Diaria, 2 dias habiles..."
                      value={form.frecuencia_lv}
                      onChange={(e) => handleChange("frecuencia_lv", e.target.value)}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#64748b",
                        display: "block",
                        mb: 1,
                        fontSize: "0.72rem",
                        letterSpacing: 0.4,
                      }}
                    >
                      DÍAS DE TRASLADO
                    </MDTypography>
                    <ToggleButtonGroup
                      value={parseDias(form.dias_traslado_lv)}
                      onChange={(e, val) => handleDiasChange("dias_traslado_lv", e, val)}
                      size="small"
                      sx={toggleGroupSx("#2e7d32")}
                    >
                      {DIAS.map((d) => (
                        <ToggleButton disabled={saving} key={d} value={d}>
                          {d}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Grid>
                </Grid>
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              {/* LIQUIDACIÓN FDS */}
              <MDBox
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  height: "100%",
                  bgcolor: "#fff",
                  border: "1px solid #dfe6ed",
                  borderRadius: 2,
                }}
              >
                <SectionTitle icon={<Weekend sx={{ fontSize: 17, color: "#ad1457" }} />}>
                  Fines de semana y festivos
                </SectionTitle>
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <TextField
                      disabled={saving}
                      fullWidth
                      size="small"
                      label="Frecuencia de liquidación"
                      placeholder="Ej: Dia habil, 2 dias habiles..."
                      value={form.frecuencia_fds}
                      onChange={(e) => handleChange("frecuencia_fds", e.target.value)}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#64748b",
                        display: "block",
                        mb: 1,
                        fontSize: "0.72rem",
                        letterSpacing: 0.4,
                      }}
                    >
                      DÍAS DE TRASLADO
                    </MDTypography>
                    <ToggleButtonGroup
                      value={parseDias(form.dias_traslado_fds)}
                      onChange={(e, val) => handleDiasChange("dias_traslado_fds", e, val)}
                      size="small"
                      sx={toggleGroupSx("#ad1457")}
                    >
                      {DIAS.map((d) => (
                        <ToggleButton disabled={saving} key={d} value={d}>
                          {d}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Grid>
                </Grid>
              </MDBox>
            </Grid>
          </Grid>
          {/* PEAJES ASOCIADOS */}
          {!esConsolidado && (
            <MDBox
              sx={{
                p: { xs: 2, sm: 2.5 },
                bgcolor: "#fff",
                border: "1px solid #dfe6ed",
                borderRadius: 2,
              }}
            >
              <SectionTitle icon={<Toll sx={{ fontSize: 17, color: "#1a237e" }} />}>
                Reglas por peaje ({form.estaciones.length})
              </SectionTitle>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#fff",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <MDTypography variant="body2" sx={{ color: "#475569", mb: 2 }}>
                  Cada peaje tiene su horario y pago independiente. La frecuencia y los parámetros
                  financieros se toman de las reglas comunes de la concesión.
                </MDTypography>
                {form.estaciones.length === 0 ? (
                  <MDTypography variant="body2" color="text.secondary">
                    No hay peajes asociados a esta concesión.
                  </MDTypography>
                ) : (
                  <Grid container spacing={2}>
                    {form.estaciones.map((est, index) => (
                      <Grid item xs={12} key={index}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            border: "1px solid #e2e8f0",
                            bgcolor: "#f8fafc",
                          }}
                        >
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={4}>
                              {typeof est.activo === "boolean" && (
                                <Chip
                                  size="small"
                                  label={est.activo ? "Activo" : "Inactivo"}
                                  variant="outlined"
                                  sx={{ mb: 1 }}
                                />
                              )}
                              <TextField
                                disabled={saving}
                                fullWidth
                                size="small"
                                label="Nombre del Peaje"
                                value={est.peaje}
                                InputProps={{ readOnly: true }}
                                sx={inputSx}
                              />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <TextField
                                disabled={saving}
                                fullWidth
                                size="small"
                                label="Hora Inicio"
                                type="time"
                                value={est.hora_inicio || ""}
                                inputProps={{ step: 1 }}
                                onChange={(e) =>
                                  handleEstacionChange(
                                    index,
                                    "hora_inicio",
                                    e.target.value.length === 5
                                      ? e.target.value + ":00"
                                      : e.target.value
                                  )
                                }
                                InputLabelProps={{ shrink: true }}
                                sx={inputSx}
                              />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <TextField
                                disabled={saving}
                                fullWidth
                                size="small"
                                label="Hora Fin"
                                type="time"
                                value={est.hora_fin || ""}
                                inputProps={{ step: 1 }}
                                onChange={(e) =>
                                  handleEstacionChange(
                                    index,
                                    "hora_fin",
                                    e.target.value.length === 5
                                      ? e.target.value + ":00"
                                      : e.target.value
                                  )
                                }
                                InputLabelProps={{ shrink: true }}
                                sx={inputSx}
                              />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    disabled={saving || !esConsolidado}
                                    checked={!esConsolidado || !!est.pago_independiente}
                                    onChange={(e) =>
                                      handleEstacionChange(
                                        index,
                                        "pago_independiente",
                                        e.target.checked
                                      )
                                    }
                                    size="small"
                                  />
                                }
                                label={
                                  <MDTypography variant="caption" sx={{ fontSize: "0.75rem" }}>
                                    Pago independiente
                                  </MDTypography>
                                }
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </MDBox>
          )}
          {/* PARÁMETROS FINANCIEROS */}
          <Accordion
            expanded={esConsolidado || rulesExpanded}
            onChange={(_, expanded) => setRulesExpanded(expanded)}
            disableGutters
            elevation={0}
            sx={{
              mt: 3,
              border: "1px solid #e2e8f0",
              borderRadius: "8px !important",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={esConsolidado ? null : <ExpandMore />}>
              <MDTypography variant="body2" fontWeight="medium">
                {esConsolidado
                  ? "Reglas de la concesión · Solo lectura"
                  : "Reglas comunes de la concesión · Solo lectura"}
              </MDTypography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                "& .MuiInputBase-root.Mui-disabled": { bgcolor: "#f8fafc" },
                "& .MuiInputBase-input.Mui-disabled": {
                  color: "#1e293b",
                  WebkitTextFillColor: "#1e293b",
                  opacity: 1,
                },
                "& .MuiInputLabel-root.Mui-disabled": { color: "#475569", opacity: 1 },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#b8c7d5",
                },
                "& .MuiSelect-icon.Mui-disabled": { color: "#64748b" },
                "& .MuiInputAdornment-root": { color: "#475569" },
                "& .MuiFormHelperText-root": { color: "#475569" },
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#fff",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Grid container spacing={2.5}>
                  {concesion.rules?.corte_horario !== undefined && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        disabled
                        fullWidth
                        size="small"
                        label="Corte horario de la concesión"
                        value={String(concesion.rules.corte_horario)}
                        sx={inputSx}
                      />
                    </Grid>
                  )}
                  {typeof concesion.rules?.activo === "boolean" && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        disabled
                        fullWidth
                        size="small"
                        label="Estado de las reglas"
                        value={concesion.rules.activo ? "Activo" : "Inactivo"}
                        sx={inputSx}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      disabled
                      fullWidth
                      size="small"
                      label="Formato de Llave (llave_fmt)"
                      placeholder="Ej: HH24:MI, 00:00_11:59"
                      value={form.llave_fmt}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" sx={inputSx}>
                      <InputLabel>Tipo de Corte</InputLabel>
                      <Select disabled value={form.tipo_corte} label="Tipo de Corte">
                        {TIPOS_CORTE.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      disabled
                      fullWidth
                      size="small"
                      label="% ReteIVA"
                      type="number"
                      inputProps={{ step: 0.01, min: 0, max: 1 }}
                      value={Number((Number(form.pct_reteiva) * 100).toFixed(6))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      disabled
                      fullWidth
                      size="small"
                      label="% Comisión"
                      type="number"
                      inputProps={{ step: 0.0001, min: 0, max: 1 }}
                      value={Number((Number(form.pct_comision) * 100).toFixed(6))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      disabled
                      fullWidth
                      size="small"
                      label="% IVA Comisión"
                      type="number"
                      inputProps={{ step: 0.01, min: 0, max: 1 }}
                      value={Number((Number(form.pct_iva_comision) * 100).toFixed(6))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      disabled
                      fullWidth
                      size="small"
                      label="% ReteFuente"
                      type="number"
                      inputProps={{ step: 0.01, min: 0, max: 1 }}
                      value={Number((Number(form.pct_retefuente) * 100).toFixed(6))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small" sx={inputSx}>
                      <InputLabel>Tipo ReteICA</InputLabel>
                      <Select disabled value={form.tipo_reteica} label="Tipo ReteICA">
                        {TIPOS_RETEICA.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      disabled
                      fullWidth
                      size="small"
                      label="ReteICA Fijo"
                      type="number"
                      inputProps={{ step: 0.00001, min: 0 }}
                      value={form.reteica_fijo}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      disabled
                      fullWidth
                      size="small"
                      label="ReteICA Default"
                      type="number"
                      inputProps={{ step: 0.00001, min: 0 }}
                      value={form.reteica_default}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small" sx={inputSx}>
                      <InputLabel>Validación Fallback</InputLabel>
                      <Select
                        disabled
                        value={form.validacion_ajuste_fallback}
                        label="Validación Fallback"
                      >
                        {VALIDACIONES_FALLBACK.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* 🔥 RETEICA_MAP - Solo visible cuando tiene contenido y de solo lectura */}
                  {reteicaMapTieneContenido && (
                    <Grid item xs={12}>
                      <TextField
                        disabled
                        fullWidth
                        multiline
                        rows={4}
                        size="small"
                        label="ReteICA Map (JSON)"
                        value={reteicaMapText}
                        InputProps={{
                          readOnly: true,
                        }}
                        helperText="Mapa de reteICA por comercio (solo lectura)."
                        sx={{
                          ...inputSx,
                          "& .MuiInputBase-root": {
                            height: "auto",
                            minHeight: 100,
                            fontSize: "0.85rem",
                            bgcolor: "#f8fafc", // ← Fondo gris para indicar solo lectura
                          },
                          "& textarea": {
                            fontFamily: "monospace",
                            fontSize: "0.8rem",
                            color: "#475569",
                          },
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </AccordionDetails>
          </Accordion>
        </DialogContent>

        {/* FOOTER */}
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "#fff",
            gap: 1.5,
          }}
        >
          <Button
            onClick={handleClose}
            disabled={saving}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderColor: "#cbd5e1",
              color: "#475569",
              px: 3,
              height: 40,
              "&:hover": { borderColor: "#94a3b8", bgcolor: "#f1f5f9" },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saving || !onSave}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              bgcolor: "#1a237e",
              color: "#fff",
              px: 3,
              height: 40,
              boxShadow: "0 4px 12px rgba(26, 35, 126, 0.25)",
              "&:hover": { bgcolor: "#283593" },
            }}
          >
            {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={open && confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="confirmar-politica-title"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle id="confirmar-politica-title">Confirmar cambios</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" mb={2}>
            ¿Deseas guardar esta configuración para <strong>{form.nombre}</strong>?
          </MDTypography>
          <Box sx={{ p: 2, bgcolor: "#f4f7fa", border: "1px solid #dfe6ed", borderRadius: 2 }}>
            <MDTypography variant="body2" mb={1}>
              <strong>Modalidad:</strong>{" "}
              {esConsolidado ? "Pago consolidado" : "Pago independiente por peaje"}
            </MDTypography>
            <MDTypography variant="body2" mb={1}>
              <strong>Lunes a viernes:</strong> {form.frecuencia_lv || "Sin frecuencia"} · Traslado:{" "}
              {form.dias_traslado_lv || "Sin días seleccionados"}
            </MDTypography>
            <MDTypography variant="body2" mb={1}>
              <strong>Fines de semana y festivos:</strong> {form.frecuencia_fds || "Sin frecuencia"}{" "}
              · Traslado: {form.dias_traslado_fds || "Sin días seleccionados"}
            </MDTypography>
            <MDTypography variant="body2">
              <strong>Peajes asociados:</strong> {form.estaciones.length} ·{" "}
              {
                form.estaciones.filter((estacion) => !esConsolidado || estacion.pago_independiente)
                  .length
              }{" "}
              con pago independiente
            </MDTypography>
          </Box>
          <MDTypography variant="caption" display="block" mt={2}>
            Se actualizarán la política de pago y los horarios de los peajes asociados.
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setConfirmOpen(false)} color="secondary">
            Volver a revisar
          </Button>
          <Button
            variant="contained"
            onClick={confirmSave}
            sx={{ bgcolor: "#164b65", color: "#fff", "&:hover": { bgcolor: "#103b50" } }}
          >
            Confirmar y guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ConcesionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  concesion: PropTypes.object,
  onSave: PropTypes.func,
};

ConcesionModal.defaultProps = {
  concesion: null,
  onSave: null,
};

export default ConcesionModal;
